import React, { useMemo, useState } from "react";
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";

const SEVERITY_COLORS = {
  red: "#ef4444",
  orange: "#f59e0b",
  blue: "#3b82f6",
};

const GROUP_META = {
  klant: { label: "Klant & Oplevering", bg: "#fff7ed", color: "#9a3412" },
  workflow: { label: "Workflow", bg: "#eff6ff", color: "#1d4ed8" },
  persoonlijk: { label: "Persoonlijk", bg: "#f3f4f6", color: "#374151" },
  feedback: { label: "Feedback", bg: "#f5f3ff", color: "#6d28d9" },
};

const GROUP_ORDER = ["klant", "workflow", "persoonlijk", "feedback"];

const NotificationBell = ({ notifications = [], onDismiss, onDismissAll }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const groupedNotifications = useMemo(() => {
    const groups = new Map();
    notifications.forEach((notification) => {
      const group = notification.group || "workflow";
      if (!groups.has(group)) {
        groups.set(group, []);
      }
      groups.get(group).push(notification);
    });
    return GROUP_ORDER.filter((group) => groups.has(group)).map((group) => ({
      group,
      items: groups.get(group),
    }));
  }, [notifications]);

  const severityCounts = useMemo(
    () =>
      notifications.reduce(
        (totals, notification) => {
          totals[notification.severity] = (totals[notification.severity] || 0) + 1;
          return totals;
        },
        { red: 0, orange: 0, blue: 0 }
      ),
    [notifications]
  );

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleClick = (notification) => {
    if (notification.link) {
      navigate(notification.link);
    }
    handleClose();
  };

  const count = notifications.length;

  return (
    <>
      <Tooltip title="Meldingen">
        <IconButton
          aria-label="Meldingen"
          onClick={handleOpen}
          sx={{ color: "inherit", position: "relative" }}
        >
          <Badge
            badgeContent={count}
            color="error"
            max={99}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: 11,
                height: 18,
                minWidth: 18,
              },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 380,
              maxHeight: 520,
              borderRadius: 2,
              mt: 1,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid #e5e7eb",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Notificatiecentrum
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Prioriteiten uit klantwerk, workflow en jouw taken
            </Typography>
          </Box>
          {count > 0 && (
            <Button
              size="small"
              onClick={() => {
                onDismissAll?.();
                handleClose();
              }}
              sx={{ textTransform: "none", fontSize: 13, color: "#2563eb" }}
            >
              Alles wissen
            </Button>
          )}
        </Box>

        {count > 0 && (
          <Stack direction="row" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: "1px solid #f3f4f6" }}>
            {severityCounts.red > 0 && (
              <Chip
                size="small"
                label={`${severityCounts.red} kritiek`}
                sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 600 }}
              />
            )}
            {severityCounts.orange > 0 && (
              <Chip
                size="small"
                label={`${severityCounts.orange} binnenkort`}
                sx={{ backgroundColor: "#fef3c7", color: "#b45309", fontWeight: 600 }}
              />
            )}
            {severityCounts.blue > 0 && (
              <Chip
                size="small"
                label={`${severityCounts.blue} ter info`}
                sx={{ backgroundColor: "#dbeafe", color: "#1d4ed8", fontWeight: 600 }}
              />
            )}
          </Stack>
        )}

        {count === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#10b981", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Geen meldingen
            </Typography>
          </Box>
        ) : (
          <Box sx={{ overflowY: "auto", maxHeight: 380 }}>
            {groupedNotifications.map(({ group, items }, groupIndex) => {
              const meta = GROUP_META[group] || GROUP_META.workflow;
              return (
                <Box key={group}>
                  {groupIndex > 0 && <Divider />}
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Typography variant="overline" sx={{ letterSpacing: 0.5, color: "#6b7280" }}>
                      {meta.label}
                    </Typography>
                    <Chip
                      size="small"
                      label={items.length}
                      sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 700 }}
                    />
                  </Box>

                  <List disablePadding>
                    {items.map((notification, index) => (
                      <React.Fragment key={notification.id}>
                        {index > 0 && <Divider component="li" />}
                        <ListItem
                          onClick={() => handleClick(notification)}
                          sx={{
                            cursor: "pointer",
                            borderLeft: `4px solid ${
                              SEVERITY_COLORS[notification.severity] || "#6366f1"
                            }`,
                            "&:hover": { backgroundColor: "#f9fafb" },
                            pr: 6,
                            alignItems: "flex-start",
                          }}
                        >
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ pr: 1, lineHeight: 1.35 }}
                              >
                                {notification.title}
                              </Typography>
                            }
                            secondary={
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", mt: 0.5, lineHeight: 1.4 }}
                              >
                                {notification.subtitle}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                onDismiss?.(notification.id);
                              }}
                              sx={{ color: "#9ca3af" }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              );
            })}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
